import type * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import { ChatCollapseIcon } from './chat-drawer-icons.tsx';

interface ChatDrawerHeaderProps {
  title: string;
  subtitle?: string;
  expanded: boolean;
  /** Extra controls, left of the dismiss button. */
  action?: React.ReactNode;
  /** On a phone dismissing closes the drawer; elsewhere it collapses to the strip. */
  belowMobile: boolean;
  closeLabel: string;
  collapseLabel: string;
  onDismiss: () => void;
}

/** The drawer's title, its invitation to ask something, and its controls. */
export function ChatDrawerHeader({
  title,
  subtitle,
  expanded,
  action,
  belowMobile,
  closeLabel,
  collapseLabel,
  onDismiss,
}: ChatDrawerHeaderProps) {
  return (
    <div
      data-slot='chat-drawer-header'
      /* Clear of the status bar when the app runs from the home screen. */
      className='relative shrink-0 border-b border-line px-5.5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-4'
    >
      {/* Expanded, the header lines up with the conversation below it. */}
      <div className={cn('relative mx-auto w-full', expanded && 'max-w-190')}>
        <h2 className='pr-18 text-base font-semibold text-ink'>{title}</h2>
        {subtitle ? (
          <p className='mt-0.75 pr-18 text-sm text-ink-muted'>{subtitle}</p>
        ) : null}
      </div>
      <div className='absolute top-[max(1.125rem,calc(env(safe-area-inset-top)-0.375rem))] right-4 flex items-center gap-0.5'>
        {action}
        <IconButton
          aria-label={belowMobile ? closeLabel : collapseLabel}
          onClick={onDismiss}
        >
          {belowMobile ? (
            <X size={16} aria-hidden='true' />
          ) : (
            <ChatCollapseIcon size={16} />
          )}
        </IconButton>
      </div>
    </div>
  );
}
